import Immutable, { List } from 'immutable';
import { DateTime } from 'luxon';
import {
  ProjectDrive,
  ProjectDriveItemType,
  RawProjectDrive,
} from 'records/ProjectDrive';
import { ProjectDriveFile } from 'records/ProjectDriveFile';
import { ProjectDriveFolderItems } from 'records/ProjectDriveFolderItems';
import { ProjectDriveSourceTarget } from 'records/ProjectDriveSourceTarget';
import {
  ProjectDriveSyncStatus,
  ProjectDriveSyncStatusType,
} from 'records/ProjectDriveSyncStatus';
import GQLRequest from 'utils/GQLRequest';

type ProjectDriveDetails = {
  id: string;
  name: string;
  ownerid: string;
  projectid: string;
  sources?: {
    connectionid: string;
    providertype: string;
    connectionprincipal: string;
    sources: {
      itemid: string;
      itemtype: ProjectDriveItemType;
    }[];
  };
};

type GetProjectDriveIdByConnectionIdResponse = {
  matchingDriveId?: string;
  lastSync?: string;
};

export const getProjectDriveMetaDataByConnectionId = async (
  projectId: string,
  connectionId: string
): Promise<GetProjectDriveIdByConnectionIdResponse | undefined> => {
  const request = {
    projectid: projectId,
  };

  const { getProjectDrives: projectDrivesDetails } = await new GQLRequest()
    .query(
      `
      query($request: GetProjectDrivesRequest!){
        getProjectDrives(request: $request){
          id
          lastSync {
            createdat
          }
          sources {
            connectionid
          }   
        }
      }
      `
    )
    .variables({ request })
    .json<{
      getProjectDrives: {
        id: string;
        lastSync: {
          createdat: string;
        };
        sources: {
          connectionid: string;
        };
      }[];
    }>();

  const matchingDrive = List(projectDrivesDetails).find(
    (detail) => detail.sources.connectionid === connectionId
  );

  const currentDate = new Date();
  const formattedDate = currentDate.toISOString().replace('Z', '+00:00');

  return {
    matchingDriveId: matchingDrive?.id,
    lastSync: matchingDrive?.lastSync?.createdat
      ? matchingDrive?.lastSync.createdat
      : formattedDate,
  };
};

export const getProjectDrives = async (
  selectedProject: string
): Promise<Immutable.List<ProjectDrive>> => {
  const request = {
    projectid: selectedProject,
  };

  const { getProjectDrives: projectDrivesDetails } = await new GQLRequest()
    .query(
      `
      query($request: GetProjectDrivesRequest!){
        getProjectDrives(request: $request){
          id
          name
          ownerid
          projectid
          sources {
            connectionid
            providertype
            connectionprincipal
            sources {
              itemid
              itemtype
            }
          }   
        }
      }
      `
    )
    .variables({ request })
    .json<{
      getProjectDrives: ProjectDriveDetails[];
    }>();

  const projectDrives: RawProjectDrive[] = projectDrivesDetails
    .filter((details) => details.sources)
    .map((details) => {
      const {
        id: driveId,
        projectid: projectId,
        name,
        ownerid: ownerId,
        sources: driveSources,
      } = details;

      const {
        connectionid: connectionId,
        providertype: providerType,
        connectionprincipal: connectionPrincipal,
        sources: sourceTargets,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      } = driveSources!;

      return {
        driveId,
        projectId,
        name,
        ownerId,
        connectionId,
        providerType,
        connectionPrincipal,
        sources: sourceTargets.map(({ itemid, itemtype }) => ({
          itemId: itemid,
          itemType: itemtype as ProjectDriveItemType,
        })),
      };
    });

  return Immutable.List(projectDrives.map(ProjectDrive.deserialize));
};

const ProjectDriveFolderItemsQuery = `
            items {
              count
              documents {
                createdAt
                updatedAt
                fileHash
                id
                name
                size
                key
                committed
              }
              folders {
                id
                name
                key
                createdat
                updatedat
              }
            }
`;

type ProjectDriveDocument = {
  createdAt: string;
  updatedAt: string;
  fileHash: string;
  id: string;
  name: string;
  size: number;
  key: string;
  committed: boolean;
};

type ProjectDriveFolderItemsResponse = {
  count: string;
  documents: ProjectDriveDocument[];
  folders: {
    id: string;
    key: string;
    name: string;
    createdat: string;
    updatedat: string;
  }[];
};

const transformProjectDriveFolderItemsResponse = (
  id: string,
  response: ProjectDriveFolderItemsResponse
): ProjectDriveFolderItems => {
  const { count, folders, documents } = response;

  return ProjectDriveFolderItems.deserialize({
    id,
    count: Number(count),
    folders: folders.map(
      ({ id: folderId, key, name, updatedat, createdat }) => ({
        id: folderId,
        key,
        name,
        createdAt: createdat,
        updatedAt: updatedat,
      })
    ),
    documents,
  });
};

export const getProjectDriveItems = async (
  selectedProject: string,
  driveId: string
): Promise<ProjectDriveFolderItems> => {
  const request = {
    projectid: selectedProject,
    driveid: driveId,
  };

  const { getProjectDrive } = await new GQLRequest()
    .query(
      `
      query($request: GetProjectDriveRequest!){
        getProjectDrive(request: $request){
          ${ProjectDriveFolderItemsQuery}
        }
      }
      `
    )
    .variables({ request })
    .json<{
      getProjectDrive: { items: ProjectDriveFolderItemsResponse };
    }>();

  return transformProjectDriveFolderItemsResponse(
    driveId,
    getProjectDrive.items
  );
};

export const getProjectDriveFolderItems = async (
  selectedProject: string,
  driveId: string,
  folderId: string
): Promise<ProjectDriveFolderItems> => {
  const request = {
    projectid: selectedProject,
    driveid: driveId,
  };

  const { getProjectDrive } = await new GQLRequest()
    .query(
      `
      query($request: GetProjectDriveRequest!, $folderId: String!){
        getProjectDrive(request: $request){
          folder(folderid: $folderId){
            ${ProjectDriveFolderItemsQuery}
          }
        }
      }
      `
    )
    .variables({ request, folderId })
    .json<{
      getProjectDrive: { folder: { items: ProjectDriveFolderItemsResponse } };
    }>();

  return transformProjectDriveFolderItemsResponse(
    folderId,
    getProjectDrive.folder.items
  );
};

type DriveSourceTarget = {
  itemType: 'file' | 'folder';
  itemId: string;
  driveId: string;
  folderPathIds: string[];
  siteId?: string;
};

export interface DriveSource {
  connectedAccountId: string;
  targets: DriveSourceTarget[];
}

const addProjectDrive = async (
  projectId: string,
  label: string,
  driveSource: DriveSource
): Promise<{ success: boolean; driveId: string }> => {
  const lowerCaseDriveSource = {
    connectedaccountid: driveSource.connectedAccountId,
    targets: driveSource.targets.map(
      ({ itemType, itemId, driveId, folderPathIds, siteId }) => ({
        itemtype: itemType.toLocaleLowerCase() === 'file' ? 'File' : 'Folder',
        itemid: itemId,
        driveid: driveId,
        folderIdPath: folderPathIds,
        siteId,
      })
    ),
  };

  const {
    addProjectDrive: { success, driveid: driveId },
  } = await new GQLRequest()
    .query(
      `
      mutation($input: AddProjectDriveRequest!){
        addProjectDrive(input: $input) {
          success
          driveid
        }
      }
      `
    )
    .variables({
      input: {
        projectid: projectId,
        label,
        drivesource: lowerCaseDriveSource,
      },
    })
    .json<{
      addProjectDrive: { success: boolean; driveid: string };
    }>();

  return { success, driveId };
};

const updateProjectDriveSourceTargets = async (
  projectId: string,
  driveId: string,
  driveSourceTargets: DriveSourceTarget[]
): Promise<{ success: boolean }> => {
  const targets = driveSourceTargets.map(
    ({ itemType, itemId, driveId: targetDriveId, folderPathIds, siteId }) => ({
      itemtype: itemType.toLocaleLowerCase() === 'file' ? 'File' : 'Folder',
      itemid: itemId,
      driveid: targetDriveId,
      folderIdPath: folderPathIds,
      siteId,
    })
  );

  const {
    updateProjectDriveSourceTargets: { success },
  } = await new GQLRequest()
    .query(
      `
      mutation($input: UpdateProjectDriveSourceTargetsRequest!){
        updateProjectDriveSourceTargets(input: $input) {
          success
        }
      }
      `
    )
    .variables({
      input: {
        projectid: projectId,
        driveid: driveId,
        targets,
      },
    })
    .json<{
      updateProjectDriveSourceTargets: { success: boolean };
    }>();

  return { success };
};

export const syncProjectDrive = async (
  driveId: string,
  projectId: string
): Promise<{ success: boolean }> => {
  const {
    syncProjectDrive: { success },
  } = await new GQLRequest()
    .query(
      `
      mutation($input: SyncProjectDriveRequest!){
        syncProjectDrive(input: $input) {
          success
        }
      }
      `
    )
    .variables({
      input: {
        driveid: driveId,
        projectid: projectId,
      },
    })
    .json<{
      syncProjectDrive: { success: boolean };
    }>();

  return { success };
};

export const addProjectDriveAndSync = async (
  projectId: string,
  label: string,
  driveSource: DriveSource
): Promise<{ success: boolean }> => {
  const { driveId, success: addProjectDriveSuccess } = await addProjectDrive(
    projectId,
    label,
    driveSource
  );

  if (!addProjectDriveSuccess) {
    return { success: addProjectDriveSuccess };
  }

  const { success: syncProjectDriveSuccess } = await syncProjectDrive(
    driveId,
    projectId
  );

  return { success: syncProjectDriveSuccess };
};

export const updateProjectDriveSourceTargetsAndSync = async (
  projectId: string,
  driveId: string,
  driveSourceTargets: DriveSourceTarget[]
): Promise<{ success: boolean }> => {
  const { success: updateProjectDriveSuccess } =
    await updateProjectDriveSourceTargets(
      projectId,
      driveId,
      driveSourceTargets
    );

  if (!updateProjectDriveSuccess) {
    return { success: updateProjectDriveSuccess };
  }

  const { success: syncProjectDriveSuccess } = await syncProjectDrive(
    driveId,
    projectId
  );

  return { success: syncProjectDriveSuccess };
};

type ProjectDriveSyncStatusResponse = {
  id: string;
  createdat: string;
  scheduledat?: string;
  startedat?: string;
  completedat?: string;
  status: ProjectDriveSyncStatusType;
  resolution?: { success: boolean };
};

export const getProjectDriveSyncHistory = async (
  selectedProject: string,
  driveId: string
): Promise<List<ProjectDriveSyncStatus>> => {
  const notBefore = DateTime.local().minus({ hours: 2 }).toISO();

  const request = {
    projectid: selectedProject,
    driveid: driveId,
  };

  const { getProjectDrive } = await new GQLRequest()
    .query(
      `
      query($request: GetProjectDriveRequest!, $notBefore: String!){
        getProjectDrive(request: $request){
          driveid
          details {
            ownerid
            sources {
              providertype
            }
            syncHistory(notBefore: $notBefore) {
              id
              createdat
              scheduledat
              startedat
              completedat
              status
              resolution {
                success
              }
            }
          }
        }
      }
      `
    )
    .variables({ request, notBefore })
    .json<{
      getProjectDrive: {
        driveid: string;
        details: {
          ownerid: string;
          sources: {
            providertype: string;
          };
          syncHistory: ProjectDriveSyncStatusResponse[];
        };
      };
    }>();

  const syncStatuses = List(
    getProjectDrive.details.syncHistory.map(
      ({
        id,
        createdat,
        scheduledat,
        startedat,
        completedat,
        status,
        resolution,
      }) =>
        ProjectDriveSyncStatus.deserialize({
          id,
          driveId: getProjectDrive.driveid,
          ownerId: getProjectDrive.details.ownerid,
          providerType: getProjectDrive.details.sources.providertype,
          createdAt: createdat,
          scheduledAt: scheduledat,
          startedAt: startedat,
          completedAt: completedat,
          status,
          success: resolution?.success,
        })
    )
  );

  return syncStatuses;
};

export const searchProjectDrive = async (
  selectedProject: string,
  driveId: string,
  searchTerm: string
): Promise<List<ProjectDriveFile>> => {
  const request = {
    projectid: selectedProject,
    driveid: driveId,
  };
  searchTerm = searchTerm.toLowerCase();
  const { getProjectDrive } = await new GQLRequest()
    .query(
      `
      query($request: GetProjectDriveRequest!, $searchTerm: String!){
        getProjectDrive(request: $request){
          driveid
          projectid
          search(searchTerm: $searchTerm){
            documents {
              createdAt
              updatedAt
              fileHash
              id
              name
              size
              key
              committed
            }
          }
        }
      }
      `
    )
    .variables({ request, searchTerm })
    .json<{
      getProjectDrive: { search: { documents: ProjectDriveDocument[] } };
    }>();
  const { documents } = getProjectDrive.search;
  return List(documents.map(ProjectDriveFile.deserialize));
};

export const retryProjectDriveSyncJob = async (
  projectId: string,
  driveId: string,
  syncId: string
): Promise<void> => {
  const input = {
    projectid: projectId,
    driveid: driveId,
    syncid: syncId,
  };

  await new GQLRequest()
    .query(
      `
      mutation($input: RetryProjectDriveSyncJobRequest!){
        retryProjectDriveSyncJob(input: $input){
          syncid
        }
      }
      `
    )
    .variables({ input })
    .json<{
      retryProjectDriveSyncJob: { syncid: string };
    }>();
};

type AcknowledgedSyncId = string;
type ISOString = string;

export const acknowledgeAllProjectDriveSyncFailures = async (
  projectId: string,
  notBefore: ISOString
): Promise<List<AcknowledgedSyncId>> => {
  const request = {
    projectid: projectId,
    notBefore,
  };

  const {
    acknowledgeAllProjectDriveSyncFailures: { acknowledgedSyncs },
  } = await new GQLRequest()
    .query(
      `
      mutation($request: AcknowledgeAllProjectDriveSyncFailuresRequest!){
        acknowledgeAllProjectDriveSyncFailures(request: $request){
          acknowledgedSyncs
        }
      }
      `
    )
    .variables({ request })
    .json<{
      acknowledgeAllProjectDriveSyncFailures: { acknowledgedSyncs: string[] };
    }>();

  return List(acknowledgedSyncs);
};

export const getProjectDriveSourceTargets = async (
  projectId: string,
  driveId: string
): Promise<List<ProjectDriveSourceTarget>> => {
  const request = {
    projectid: projectId,
    driveid: driveId,
  };

  const {
    getProjectDrive: {
      details: {
        sources: { sources: driveSourceTargets },
      },
    },
  } = await new GQLRequest()
    .query(
      `
      query($request: GetProjectDriveRequest!){
        getProjectDrive(request: $request){
          details {
            sources {
              sources {
                itemid
                itemtype
                driveid
                folderIdPath
                siteId
              }
            }
          }
        }
      }
      `
    )
    .variables({ request })
    .json<{
      getProjectDrive: {
        details: {
          sources: {
            sources: {
              itemid: string;
              itemtype: 'File' | 'Folder';
              driveid: string;
              folderIdPath?: string[];
              siteId?: string;
            }[];
          };
        };
      };
    }>();

  return List(
    driveSourceTargets
      .filter((t) => t.folderIdPath)
      .map(({ itemid, itemtype, driveid, folderIdPath, siteId }) =>
        ProjectDriveSourceTarget.deserialize({
          id: itemid,
          itemType: itemtype.toLocaleLowerCase() as 'file' | 'folder',
          driveId: driveid,
          folderPathIds: folderIdPath!,
          siteId,
        })
      )
  );
};

export const getProjectDriveSourceTargetsByConnectionId = async (
  projectId: string,
  connectionId: string
): Promise<List<ProjectDriveSourceTarget>> => {
  const connection = await getProjectDriveMetaDataByConnectionId(
    projectId,
    connectionId
  );

  if (!connection || !connection.matchingDriveId) {
    return List();
  }

  return getProjectDriveSourceTargets(projectId, connection.matchingDriveId);
};
